import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react'
import { Button, Modal } from '@/components/ui'
import { useT } from '@/i18n/I18nProvider'

/**
 * "Are you sure?", in this application's own chrome.
 *
 * Every confirmation here used to be `window.confirm`, which is the one piece
 * of UI a web app cannot style, cannot translate the buttons of, and cannot
 * make safe. Three specific problems, none cosmetic:
 *
 * - **The buttons are the browser's.** "OK" and "Cancel" in the browser's
 *   language, not the app's — an English Chrome shows English buttons under a
 *   Bosnian question, and a destructive action gets the same neutral "OK" as a
 *   harmless one.
 * - **It blocks the main thread.** Everything stops: the offline queue stops
 *   draining, live updates stop arriving, and on a phone the whole page freezes
 *   behind a system sheet.
 * - **It looks like the browser, not like us.** On a shop-floor Android the
 *   system dialog is indistinguishable from a page asking permission for
 *   something, which is exactly the wrong association for "delete this".
 *
 * ## Promise-based on purpose
 *
 * `const ok = await confirm({...}); if (!ok) return` is the same shape the
 * `window.confirm` call sites already had, so converting them is a local edit
 * rather than a restructure into callbacks. That mattered: there were ten of
 * them and a rewrite of each is ten chances to change behaviour by accident.
 */
interface ConfirmOptions {
  title: string
  /** The consequence, in a sentence. Skipped when the title says it all. */
  body?: string
  /**
   * Anything the decision needs below the sentence — a list of what changes,
   * or an optional field such as a reason.
   *
   * A field here should report its value through its own `onChange` into a
   * variable the caller reads once the promise settles, rather than through a
   * ref: the dialog unmounts as it settles, and a ref read after that would be
   * reading something that is already gone.
   */
  details?: ReactNode
  /** Defaults to a neutral "confirm"; name the action where you can. */
  confirmLabel?: string
  /** Red confirm button. Anything that deletes or revokes should set this. */
  tone?: 'default' | 'danger'
}

type Confirm = (options: ConfirmOptions) => Promise<boolean>

const ConfirmContext = createContext<Confirm | null>(null)

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const t = useT()
  const [options, setOptions] = useState<ConfirmOptions | null>(null)

  /*
   * The resolver for the promise handed to the caller.
   *
   * A ref rather than state because resolving must not depend on a re-render
   * having happened — the click handler needs the *current* resolver, and a
   * stale closure over a state value would silently drop the answer and leave
   * the caller awaiting forever.
   */
  const resolver = useRef<((value: boolean) => void) | null>(null)

  const confirm = useCallback<Confirm>((next) => {
    setOptions(next)

    return new Promise<boolean>((resolve) => {
      resolver.current = resolve
    })
  }, [])

  function settle(answer: boolean) {
    resolver.current?.(answer)
    resolver.current = null
    setOptions(null)
  }

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}

      <Modal
        open={options !== null}
        // Dismissing any other way — the X, Escape, the backdrop — is a "no".
        // Leaving the promise unsettled would hang the caller.
        onClose={() => settle(false)}
        title={options?.title ?? ''}
        size="md"
        footer={
          <>
            <Button variant="secondary" onClick={() => settle(false)}>
              {t('action.cancel')}
            </Button>
            <Button
              variant={options?.tone === 'danger' ? 'danger' : 'primary'}
              onClick={() => settle(true)}
            >
              {options?.confirmLabel ?? t('action.confirm')}
            </Button>
          </>
        }
      >
        {options?.body && <p className="text-sm">{options.body}</p>}
        {options?.details && <div className="mt-3">{options.details}</div>}
      </Modal>
    </ConfirmContext.Provider>
  )
}

/**
 * Ask before doing something irreversible.
 *
 * Throws if the provider is missing rather than falling back to
 * `window.confirm`: a silent fallback would mean the thing this replaced
 * quietly returning in whichever corner of the tree somebody forgot to wrap.
 */
export function useConfirm(): Confirm {
  const confirm = useContext(ConfirmContext)

  if (!confirm) throw new Error('useConfirm must be used inside <ConfirmProvider>')

  return confirm
}
